/**
 * InCallScreen — audio 1-1 in-call UI.
 * Sprint B scaffolding : full-screen overlay with Mute, Speaker, End,
 * Record toggle, live duration timer.
 *
 * The LiveKit track attachment will be wired in Sprint B when creds arrive.
 * For now, receives a `session` prop + callbacks; the UI chrome is ready.
 *
 * Mobile-first : safe-area insets, 56px buttons, no horizontal scroll.
 */
import { useEffect, useState } from 'react';
import { Microphone, MicrophoneSlash, SpeakerHigh, SpeakerSlash, PhoneSlash, Record } from '@phosphor-icons/react';
import './calls.css';
import { CallControl, CallBackButton } from './CallControls';
import { useTranslation } from 'react-i18next';

function fmt(sec) {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const mm = String(m % 60).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export default function InCallScreen({
  session,
  peerName = '',
  peerAvatar = '',
  muted = false,
  speakerOn = true,
  recording = false,
  connectionStatus = 'connected',   // connecting | connected | reconnecting | failed
  canRecord = false,                // host-only
  callType = 'audio',               // iter250a — 'audio' | 'video'
  onToggleMute,
  onToggleSpeaker,
  onToggleRecord,
  onHangup,
  onMinimize,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = (i18n?.language || '').startsWith('ar');
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!session?.started_at || connectionStatus !== 'connected') return;
    const start = new Date(session.started_at).getTime();
    const tick = () => setElapsed((Date.now() - start) / 1000);
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [session?.started_at, connectionStatus]);

  if (!session) return null;
  const initial = (peerName?.[0] || '?').toUpperCase();
  const isConnecting = connectionStatus !== 'connected';
  const isVideo = callType === 'video';

  return (
    <div
      className={`jp-call-overlay ${callType === 'video' ? '' : 'jp-call-light'}`}
      data-testid="in-call-screen"
      role="dialog"
      aria-modal="true"
      dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="jp-call-header">
        <CallBackButton onClick={onMinimize} />
        <span>
          {recording && (
            <span data-testid="recording-indicator" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span className="jp-rec-dot" /> REC
            </span>
          )}
        </span>
        <span className="jp-call-title">{isVideo ? t('calls.video_call') : t('calls.audio_call')}</span>
        <span>{connectionStatus === 'reconnecting' ? t('calls.connecting') : ''}</span>
      </div>

      <div className="jp-call-body">
        <div className="jp-call-avatar">
          {peerAvatar ? (
            <img src={peerAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
          ) : initial}
        </div>
        <p className="jp-call-name">{peerName}</p>
        <p className="jp-call-status">
          {isConnecting ? t('calls.connecting') : (
            <span className="jp-call-timer" data-testid="call-duration">{fmt(elapsed)}</span>
          )}
        </p>
      </div>

      <div className="jp-call-controls">
        <div className="jp-call-controls-row">
          <CallControl onClick={onToggleMute} data-testid="call-mute-btn" aria-pressed={muted}
            label={muted ? t('web.call_unmute') : t('web.call_mute')} className={muted ? 'jp-call-btn-active' : ''}>
            {muted ? <MicrophoneSlash size={24} weight="fill" /> : <Microphone size={24} weight="fill" />}
          </CallControl>
          <CallControl onClick={onToggleSpeaker} data-testid="call-speaker-btn" aria-pressed={speakerOn}
            label={t('web.call_speaker')} className={speakerOn ? 'jp-call-btn-active' : ''}>
            {speakerOn ? <SpeakerHigh size={24} weight="fill" /> : <SpeakerSlash size={24} weight="fill" />}
          </CallControl>
          {canRecord && (
            <CallControl onClick={onToggleRecord} data-testid="call-record-btn" aria-pressed={recording}
              label={recording ? t('web.call_rec_stop') : t('web.call_rec_start')} className={recording ? 'jp-call-btn-active' : ''}>
              <Record size={24} weight="fill" color={recording ? '#E01C2E' : undefined} />
            </CallControl>
          )}
          <CallControl onClick={onHangup} data-testid="call-hangup-btn" label={t('web.call_hangup')} className="jp-call-btn-end">
            <PhoneSlash size={26} weight="fill" />
          </CallControl>
        </div>
      </div>
    </div>
  );
}
